import { jsPDF } from "jspdf";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { Grid, Icon } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";
import { gql, useLazyQuery } from "@apollo/client";
import EmpleadoLayout from "components/LayoutContainers/EmpleadoLayout";
import boxShadows from "assets/theme/base/boxShadows";
import EditableText from "components/EditableText";
import PdfHeader from "../components/pdfHeader";
import { formatDate } from "utils/formatters";
import { formatMoneyPunto } from "utils/formatters";

const textosIniciales = [
  "", //fecha1
  "", //fecha2
  "...", //observaciones
];

const PreventivoPDF = () => {
  const documentsRef = useRef(null);
  const { id } = useParams();
  const { lg } = boxShadows;

  const [data, setData] = useState(null);
  const [textArray, setTextArray] = useState(textosIniciales);
  const [editable, setEditable] = useState(true);

  const [getPedido, { loading }] = useLazyQuery(
    gql`
        query getPedido($id: ID) {
          pedidosCompra(filter: { id: $id }) {
            id
            numeroExpediente
            fechaSolicitud
            objetoCompra
            presupuesto{
              secretaria{
                codigo
              }
              clasificacion
              preventivo
            },
            detalles {
              cantidadVerificada
              precioEstimado
            }
          }
        }
      `,
    {
      onCompleted: (data) => {
        if (!data?.pedidosCompra[0]) return;
        setData(data.pedidosCompra[0]);
      },
    }
  );

  useEffect(() => {
    if (id) {
      getPedido({ variables: { id } });
    }
  }, [id]);

  useEffect(() => {
    if (data) {
      const newTextArray = [...textArray];
      //newTextArray[0] = "";
      newTextArray[1] = formatDate(data.fechaSolicitud, "dd/MM/yyyy");

      setTextArray(newTextArray);
    }
  }, [data]);

  const handleGeneratePdf = async () => {
    setEditable(false);
    const document = documentsRef.current.childNodes[0];
    const doc = new jsPDF({
      format: "a4",
      unit: "px",
      orientation: "portrait",
      hotfixes: ["px_scaling"],
    });

    await doc.html(document, {
      margin: [50, 0, 50, 0],
      callback(doc) {
        doc.save(`exd-pedidoNro-${data.id}-Preventivo.pdf`);
      },
    });
    setEditable(true);
  };

  const handleTextChange = (e, index) => {
    const newTextArray = [...textArray];
    newTextArray[index] = e.target.innerText;
    setTextArray(newTextArray);
  };

  const totals = useMemo(() => {
    let totals = {
      totalEstimado : 0,
      preventivo: 0
    };

    if (!data) return totals;

    for (const detalle of data.detalles) {
      if (detalle.precioEstimado) {
        totals.totalEstimado += detalle.precioEstimado * (detalle.cantidadVerificada || 1);
      }
    }

    for (const presupuesto of data.presupuesto || []) {
      if (presupuesto.preventivo) {
        totals.preventivo += presupuesto.preventivo;
      }
    }

    return totals;
  }, [data]);

  return (
    <EmpleadoLayout>
      <SoftBox
        p={2}
        display="flex"
        alignItems="center"
        flexDirection="column"
        minHeight="100vh"
        height="100%"
      >
        <div ref={documentsRef}>
          <SoftBox
            sx={{
              position: "relative",
              overflow: "hidden",
              width: "794px",
              backgroundColor: "#ffffff",
              boxShadow: lg,
              px: 6,
            }}
            pb={5}
          >
            {/* ACA VA TODO EL CODIGO */}
            <PdfHeader />

            <SoftBox pb={5}>
              <Grid container>
                <Grid item xs={4}></Grid>
                <Grid item xs={4}>
                  <SoftTypography variant="subtitle2" textAlign="center" fontWeight="bold">
                    * *COMPROBANTE* *
                  </SoftTypography>
                </Grid>
                <Grid item xs={4} textAlign="end">
                  <SoftTypography variant="caption">Asiento Presup</SoftTypography>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={4}>
                  <SoftTypography variant="caption">Anulado por asiento:</SoftTypography>
                </Grid>
                <Grid item xs={4}>
                  <SoftTypography variant="caption">
                    Fecha:{" "}
                    <EditableText
                      tooltip="Fecha. Ej: (MES de AÑO)"
                      editable={editable}
                      content={textArray[0]}
                      handleOnBlur={(e) => handleTextChange(e, 0)}
                    />
                  </SoftTypography>
                </Grid>

                <Grid item xs={4} textAlign="end"></Grid>
              </Grid>
              <Grid container>
                <Grid item xs={4}>
                  <SoftTypography variant="caption">Último asiento de ajuste:</SoftTypography>
                </Grid>
                <Grid item xs={4}>
                  <SoftTypography variant="caption">
                    Fecha:{" "}
                    <EditableText
                      tooltip="Fecha. Ej: (MES de AÑO)"
                      editable={editable}
                      content={textArray[1]}
                      handleOnBlur={(e) => handleTextChange(e, 1)}
                    />
                  </SoftTypography>
                </Grid>
                <Grid item xs={4} textAlign="end"></Grid>
              </Grid>
              <Grid container>
                <Grid item xs={4}></Grid>
                <Grid item xs={4}></Grid>
                <Grid item xs={4} textAlign="end">
                  <SoftTypography variant="subtitle2" textAlign="end" fontWeight="bold">
                    ORIGINAL
                  </SoftTypography>
                </Grid>
              </Grid>
              <SoftTypography variant="body2" textAlign="center" fontWeight="bold">
                PREVENTIVO
              </SoftTypography>
              <Grid container pb={2}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    borderBottom: "1px solid black",
                    borderLeft: "1px solid black",
                    borderRight: "1px solid black",
                    fontSize: "0.8rem",
                  }}
                  textAlign="center"
                  p={3}
                  lineHeight={1.2}
                >
                  {data?.objetoCompra ?? "Objeto de compra no especificado."}
                </Grid>
              </Grid>
              <Grid
                xs={12}
                sx={{
                  borderBottom: "1px solid black",
                  borderTop: "1px solid black",
                  borderLeft: "1px solid black",
                  borderRight: "1px solid black",
                }}
                px={1}
                pb={2}
                mb={2}
              >
                <SoftTypography variant="body2">Observaciones:
                  <EditableText
                    editable={editable}
                    content={textArray[2]}
                    handleOnBlur={(e) => handleTextChange(e, 2)}
                  /> </SoftTypography>
              </Grid>
              <Grid container pb={2}>
                <Grid xs={3}></Grid>
                <Grid
                  xs={6}
                  sx={{
                    borderBottom: "1px solid black",
                    borderTop: "1px solid black",
                    borderLeft: "1px solid black",
                    borderRight: "1px solid black",
                    backgroundColor: "rgba(206, 206, 206)",
                  }}
                >
                  <SoftTypography variant="body2" fontWeight="bold" textAlign="center">
                    $ {formatMoneyPunto(totals.preventivo || 0)}
                  </SoftTypography>
                </Grid>

                <Grid xs={3}></Grid>
              </Grid>
              <Grid
                container
                sx={{
                  borderBottom: "1px solid black",
                  borderTop: "1px solid black",
                  borderLeft: "1px solid black",
                  borderRight: "1px solid black",
                  backgroundColor: "rgba(230, 230, 230)",
                  fontSize: "0.75rem",
                }}
                mb={2}
              >
                <Grid item xs={(4 / 22) * 12} textAlign="center" sx={{ height: "auto" }}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    INSTITUCIONAL
                  </SoftTypography>
                </Grid>
                <Grid item xs={(4 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption" fontWeight="bold">
                    PROGRAMÁTICA
                  </SoftTypography>
                </Grid>
                <Grid item xs={(3 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption" fontWeight="bold">
                    UPRO SECRET.
                  </SoftTypography>
                </Grid>
                <Grid item xs={(3 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption" fontWeight="bold">
                    OBJETO
                  </SoftTypography>
                </Grid>
                <Grid item xs={(8 / 22) * 12}></Grid>
                <Grid item xs={(1 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">Inst.</SoftTypography>
                </Grid>
                <Grid item xs={(1 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">Jur. j</SoftTypography>
                </Grid>
                <Grid item xs={(1 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">AU.</SoftTypography>
                </Grid>
                <Grid item xs={(1 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">Ej.</SoftTypography>
                </Grid>
                <Grid item xs={(1 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">Pr</SoftTypography>
                </Grid>
                <Grid item xs={(1 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">Sp</SoftTypography>
                </Grid>
                <Grid item xs={(1 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">Py</SoftTypography>
                </Grid>
                <Grid item xs={(1 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">Ac</SoftTypography>
                </Grid>
                <Grid item xs={(1 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">D</SoftTypography>
                </Grid>
                <Grid item xs={(1 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">L</SoftTypography>
                </Grid>
                <Grid item xs={(1 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">S</SoftTypography>
                </Grid>
                <Grid item xs={(1 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">Inc</SoftTypography>
                </Grid>
                <Grid item xs={(1 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">Pri</SoftTypography>
                </Grid>
                <Grid item xs={(1 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">Parc</SoftTypography>
                </Grid>
                <Grid item xs={(2 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption">Subp</SoftTypography>
                </Grid>
                <Grid item xs={(2 / 22) * 12} textAlign="center"></Grid>
                <Grid item xs={(4 / 22) * 12} textAlign="center">
                  <SoftTypography variant="caption" fontWeight="bold">
                    IMPORTE
                  </SoftTypography>
                </Grid>
              </Grid>
              <SoftBox
                borderLeft="1px solid black"
                borderRight="1px solid black"
                borderTop="1px solid black"
              >
                {data?.presupuesto?.map((presupuesto, index) => {
                  const clasificacion = presupuesto.clasificacion.toString() ?? '';
                  const objeto = [
                    clasificacion.slice(0, 1),
                    clasificacion.slice(1, 2),
                    clasificacion.slice(2, 4),
                    clasificacion.slice(4, 6),
                  ];
                  return (
                    <Grid
                      key={index}
                      container
                      sx={{
                        borderBottom: "1px solid black",
                        fontSize: "0.75rem",
                      }}
                    >
                      <Grid item xs={(1 / 22) * 12} textAlign="center">
                        <SoftTypography variant="caption">0</SoftTypography>
                      </Grid>
                      <Grid item xs={(1 / 22) * 12} textAlign="center">
                        <SoftTypography variant="caption">00</SoftTypography>
                      </Grid>
                      <Grid item xs={(1 / 22) * 12} textAlign="center">
                        <SoftTypography variant="caption">00</SoftTypography>
                      </Grid>
                      <Grid item xs={(1 / 22) * 12} textAlign="center">
                        <SoftTypography variant="caption">00</SoftTypography>
                      </Grid>
                      <Grid item xs={(1 / 22) * 12} textAlign="center">
                        <SoftTypography variant="caption">00</SoftTypography>
                      </Grid>
                      <Grid item xs={(1 / 22) * 12} textAlign="center">
                        <SoftTypography variant="caption">00</SoftTypography>
                      </Grid>
                      <Grid item xs={(1 / 22) * 12} textAlign="center">
                        <SoftTypography variant="caption">00</SoftTypography>
                      </Grid>
                      <Grid item xs={(1 / 22) * 12} textAlign="center">
                        <SoftTypography variant="caption">00</SoftTypography>
                      </Grid>
                      <Grid item xs={(1 / 22) * 12} textAlign="center">
                        {presupuesto.secretaria.codigo || "00"}
                      </Grid>
                      <Grid item xs={(1 / 22) * 12} textAlign="center">
                        0
                      </Grid>
                      <Grid item xs={(1 / 22) * 12} textAlign="center">
                        0
                      </Grid>
                      <Grid item xs={(1 / 22) * 12} textAlign="center">
                        {objeto[0]}
                      </Grid>
                      <Grid item xs={(1 / 22) * 12} textAlign="center">
                        {objeto[1]}
                      </Grid>
                      <Grid item xs={(1 / 22) * 12} textAlign="center">
                        {objeto[2]}
                      </Grid>
                      <Grid item xs={(2 / 22) * 12} textAlign="center">
                        <SoftTypography variant="caption">
                          {objeto[3]}
                        </SoftTypography>
                      </Grid>
                      <Grid item xs={(2 / 22) * 12} textAlign="end">
                        
                      </Grid>
                      <Grid item xs={(4 / 22) * 12} textAlign="center">
                        $ {formatMoneyPunto(presupuesto.preventivo || 0)}
                      </Grid>
                    </Grid>
                  )
                })}
              </SoftBox>
              <Grid container paddingY={2}>
                <Grid xs={8}></Grid>
                <Grid
                  xs={4}
                  sx={{
                    borderBottom: "1px solid black",
                    borderTop: "1px solid black",
                    borderLeft: "1px solid black",
                    borderRight: "1px solid black",
                    backgroundColor: "rgba(206, 206, 206)",
                  }}
                  display="flex"
                  justifyContent="space-around"
                >
                  <SoftTypography variant="caption" fontWeight="bold" textAlign="center">
                    TOTAL
                  </SoftTypography>
                  <SoftTypography variant="caption" fontWeight="bold" textAlign="center">
                    $ {formatMoneyPunto(totals.preventivo || 0)}
                  </SoftTypography>
                </Grid>
              </Grid>
              <Grid container pb={2}>
                <Grid
                  xs={12}
                  sx={{
                    borderBottom: "1px solid black",
                    borderTop: "1px solid black",
                    borderLeft: "1px solid black",
                    borderRight: "1px solid black",
                    backgroundColor: "rgba(206, 206, 206)",
                  }}
                  display="flex"
                  justifyContent="end"
                  alignItems="center"
                  pr={4}
                  gap={6}
                >
                  <SoftTypography variant="button">
                    TOTAL GENERAL DE IMPUTACIÓN
                  </SoftTypography>
                  <SoftTypography variant="body2" fontWeight="bold" textAlign="center">
                    $ {formatMoneyPunto(totals.preventivo || 0)}
                  </SoftTypography>
                </Grid>
              </Grid>
            </SoftBox>
          </SoftBox>
        </div>
        <SoftBox
          variant="contained"
          bgColor="white"
          p={2}
          style={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            zIndex: 1000,
            boxShadow:
              "inset 0rem 0rem 0.0625rem 0.0625rem rgb(255 255 255 / 90%), 0rem 1.25rem 1.6875rem 0rem rgb(0 0 0 / 5%)",
            borderRadius: "16px",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: `saturate(200%) blur(${pxToRem(30)})`,
          }}
        >
          <Grid container spacing={2} justifyContent="flex-end" id="button">
            <Grid item>
              <SoftButton variant="gradient" color="error" onClick={handleGeneratePdf}>
                <Icon fontWeight="bold">picture_as_pdf</Icon>
                &nbsp;&nbsp;Descargar PDF
              </SoftButton>
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
    </EmpleadoLayout>
  );
};

export default PreventivoPDF;
